import type { ComponentLibrary } from '@suntex.classic/adobe-animate';
import { UIUtil } from '@suntex.core/animation';
import { LogLevel, Logger } from '@suntex.core/logging';
import { Entities_Player_ProgressSummary_Response } from '@suntex.fim/api-client';
import {
  $BonusLevel,
  $GradeLevel,
  $KnowAndShowLevel,
  $SkillSetLevel,
} from '@suntex.fim/enums';
import { buildUnderConstructionSet } from '@suntex.fim/framework';

import { rootRadialData } from './const';
import { bonusNode } from './const/model/areas/bonus';
import { ksNode } from './const/model/areas/ks';
import { ssNode } from './const/model/areas/ss';
import { modulesNode } from './const/model/views/modules';
import { stagesOfLearningNode } from './const/model/views/stages-of-learning';
import { Hub } from './display/Hub';
import { ImageIcon } from './display/ImageIcon';
import { InfoPane } from './display/InfoPane';
import { Radial } from './display/Radial';
import { BuilderDependencies, IHub, ModuleTagging, SSInfoPane } from './types';
import { applyProgress } from './utils/applyProgress';
import { buildChildrenFromJSON } from './utils/buildChildrenFromJson';
import { buildModuleEnabledPredicate } from './utils/buildModuleEnabledPredicate';
import { buildModuleSlugSet } from './utils/buildModuleSlugSet';
import { createGetHref } from './utils/getHref';

declare global {
  interface Window {
    hub?: IHub;
  }
}

const logger = Logger.for(['FIMHub_prog'], { level: LogLevel.Debug });

const HUB_CENTER_X = 300;
const HUB_CENTER_Y = 300;
const SKILL_SET_GAMES_MAX = 3;
const SECONDS_PER_MINUTE = 60;

export function FIMHub_prog(
  stage: createjs.Stage,
  lib: ComponentLibrary,
  config: {
    preview: boolean;
    fontFamily: string;
    moduleTagging: readonly ModuleTagging[];
  },
) {
  /* Properties */
  let hub!: Hub;

  const goldStarInfoPane = new InfoPane(
    {
      header: 'Congratulations!',
      body: "A gold star means you've\nearned all available stickers!",
    },
    {
      fontFamily: config.fontFamily,
    },
  );
  const getHref = createGetHref(config.preview);
  const deps: BuilderDependencies = {
    getHref,
    lib,
  };

  init();

  window.hub = {
    display: ({
      path,
      focus,
      enable,
      disable,
      gradeLevel,
      progressSummary,
      settings,
      onPathChange,
      setPath,
      navigate,
    }) => {
      logger.debug('display', { path, enable, disable, focus, gradeLevel });
      logger.debug('display settings', { ...settings });

      hub.config = {
        ...hub.config,
        setPath,
        navigate,
      };

      const rootRadial = hub.root;
      const stagesOfLearningRadial =
        rootRadial.getRadialChild(stagesOfLearningNode);
      const modulesRadial = rootRadial.getRadialChild(modulesNode);
      if (!stagesOfLearningRadial || !modulesRadial) {
        throw new Error('Required radials not found');
      }
      const skillSetsRadial = modulesRadial.getRadialChild(ssNode);
      const ksRadial = modulesRadial.getRadialChild(ksNode);
      const bonusRadial = modulesRadial.getRadialChild(bonusNode);
      if (!skillSetsRadial || !ksRadial || !bonusRadial) {
        throw new Error('Required module radials not found');
      }

      const isModuleEnabled = buildModuleEnabledPredicate({
        moduleTagging: config.moduleTagging,
        enable,
        disable,
      });

      // Set enabled state
      hub.root.checkEnabled(isModuleEnabled);

      // Set emphasis state BEFORE focus so updateVisualState uses correct emphasis
      stagesOfLearningRadial.setEmphasized(true);

      if (gradeLevel !== undefined) {
        stagesOfLearningRadial.radialChildren.forEach((c) => {
          c.setEmphasized(c.tag === $GradeLevel.describe(gradeLevel).slug);
        });
        skillSetsRadial.radialChildren.forEach((c) => {
          const tag = c.tag;
          if (tag) {
            c.setEmphasized($SkillSetLevel.fromSlug(tag) - gradeLevel === 0);
          }
        });
        ksRadial.radialChildren.forEach((c) => {
          const tag = c.tag;
          if (tag) {
            c.setEmphasized($KnowAndShowLevel.fromSlug(tag) - gradeLevel === 0);
          }
        });
        bonusRadial.radialChildren.forEach((c) => {
          const tag = c.tag;
          if (tag) {
            c.setEmphasized($BonusLevel.fromSlug(tag) - gradeLevel === 0);
          }
        });
      } else {
        stagesOfLearningRadial.radialChildren.forEach((c) => {
          c.setEmphasized(false);
        });
        skillSetsRadial.radialChildren.forEach((c) => {
          c.setEmphasized(false);
        });
        ksRadial.radialChildren.forEach((c) => {
          c.setEmphasized(false);
        });
        bonusRadial.radialChildren.forEach((c) => {
          c.setEmphasized(false);
        });
      }

      // Set focus state
      if (focus) {
        const modulesInFocus = buildModuleSlugSet(focus, config.moduleTagging);
        hub.root.checkInFocus(
          (slug) => modulesInFocus.has(slug) && isModuleEnabled(slug),
        );
      } else {
        // No focus selected - everything enabled is also in focus
        hub.root.checkInFocus((slug) => isModuleEnabled(slug));
      }

      hub.onCentralize = (radial) => {
        if (radial.tag !== undefined) {
          onPathChange?.(radial.getFullPath());
        }
      };

      if (settings) {
        hub.applyHubSettings(settings);
      }

      // Apply starring if progress data is provided
      if (progressSummary) {
        applyProgress(hub.root, progressSummary, goldStarInfoPane);
        displaySkillSetTimes(lib, skillSetsRadial, progressSummary);
      }

      // Mark under-construction modules last, so the construction icon,
      // disabled state, and star suppression aren't overwritten by the
      // enabled/focus/progress passes above.
      const underConstruction = buildUnderConstructionSet(config.moduleTagging);
      if (underConstruction.size > 0) {
        hub.root.checkConstruction((slug) => underConstruction.has(slug));
      }

      // Centralizes the given radial, walking up to the first enabled
      // ancestor if the radial itself is not enabled.
      function centralizeEnabledAncestor(radial: Radial): void {
        let target = radial;
        while (!target.enabled && target.radialParent) {
          target = target.radialParent;
        }
        hub.centralizeRadial(target);
      }

      if (path === '' || path === '/') {
        hub.centralizeRadial(rootRadial);
      } else {
        // Prefer an exact key match, since keys are unique across the whole
        // tree (including filtered views), unlike tags which can collide.
        const keyMatch = rootRadial.getByKey(path);

        if (keyMatch) {
          centralizeEnabledAncestor(keyMatch);
        } else {
          const pathArr = path.replace(/^\//, '').split('/');
          const [first, ...rest] = pathArr;

          // Find a descendent matching the first path segment
          const searchRoot = rootRadial.getFirstDescendent({ tag: first });

          if (searchRoot) {
            // Drill down from that descendent using the rest of the path
            centralizeEnabledAncestor(searchRoot.getDescendentOrSelf(rest));
          } else {
            hub.centralizeRadial(modulesRadial);
          }
        }
      }
    },
    getStarStats: () => {
      const centralRadial = hub.centralRadial;
      if (centralRadial && centralRadial.radialChildren.length > 0) {
        const stats = centralRadial.getStarStats();
        if (stats.totalStars > 0) {
          return stats;
        }
      }
      return null;
    },
    dispose: () => {
      logger.debug('Disposing hub');

      // Remove hub from stage to prevent phantom interactions
      stage.removeChild(hub);

      // Clean up internal hub state (radials, event listeners, etc.)
      hub.dispose();

      // Clear global reference to allow garbage collection
      window.hub = undefined;
    },
  };

  function init() {
    const iconImage = rootRadialData.icon.image;
    if (!iconImage) throw new Error('Root radial icon image not found');
    const rootRadial = new Radial(
      lib,
      0,
      0,
      0,
      rootRadialData.color,
      new ImageIcon(iconImage),
      rootRadialData.tag ?? null,
      null,
      0,
      null,
      {
        key: rootRadialData.key,
        ariaLabel: rootRadialData.ariaLabel,
        hideProgressBar: rootRadialData.hideProgressBar,
      },
    );

    buildChildrenFromJSON(
      rootRadial,
      rootRadialData.children,
      {
        fontFamily: config.fontFamily,
        preview: config.preview,
        layout: rootRadialData.layout,
        moduleTagging: config.moduleTagging,
      },
      deps,
    );

    hub = new Hub(rootRadial, HUB_CENTER_X, HUB_CENTER_Y);
    stage.addChild(hub);

    const ksInGI = [
      [1, 1, 1, 1],
      [1, 1, 1, 1],
      [1, 1, 1, 1, 0, 0, 1, 0, 0],
      [1, 1, 1, 1, 0, 0, 0, 1, 0],
      [1, 1, 1, 1, 0, 0, 1, 0, 0],
      [1, 1, 0, 0, 1, 1, 0, 1, 0],
      [0, 1, 1, 1, 1, 0, 1, 0, 0],
      [0, 0, 0, 1, 1, 1, 1, 1, 0],
    ] as const;

    const modulesRadial = rootRadial.getRadialChild(modulesNode);
    const ksRadial = modulesRadial?.getRadialChild(ksNode);
    if (!modulesRadial || !ksRadial) {
      throw new Error('Required radials not found for initialization');
    }

    modulesRadial.setEmphasized(true);

    ksRadial.radialChildren.forEach((c, i) => {
      c.radialChildren.forEach((c2, ii) => {
        const row = ksInGI[i];
        const isEmphasized = row ? row[ii] === 1 : false;
        c2.setEmphasized(isEmphasized);
      });
    });
  }

  return hub;
}

function displaySkillSetTimes(
  lib: ComponentLibrary,
  skillSetRadial: Radial,
  progressSummary: Entities_Player_ProgressSummary_Response,
): void {
  for (const ss of $SkillSetLevel.describeAll()) {
    const setRadial = skillSetRadial.getRadialChild({ tag: ss.slug });
    if (!setRadial) continue;
    const infoPane = UIUtil.createDisplayObject<SSInfoPane>(lib, 'SSInfoPane');
    let showSetBadge = true;

    infoPane.x = 40;
    setRadial.setInfoPane(infoPane);

    const GAME_MIN = 1;
    const GAME_MAX = SKILL_SET_GAMES_MAX;
    for (let g = GAME_MIN; g <= GAME_MAX; g++) {
      const gameNum = g;
      const gameInfoKey = `g${gameNum}Info` as `g1Info` | `g2Info` | `g3Info`;
      const data = progressSummary.ss[ss.value]?.[gameNum] ?? {
        star: 0,
        totalTime: 0,
      };

      const skillText = ss.skills[gameNum - 1];
      if (skillText) {
        infoPane[gameInfoKey].skillsText.text = skillText;
      }

      if (data.star < 1) {
        showSetBadge = false;
      }

      if (data.totalTime) {
        infoPane[gameInfoKey].totalTime.visible = true;
        infoPane[gameInfoKey].totalTime.text = secondsToString(data.totalTime);
      }
    }

    if (showSetBadge) {
      UIUtil.setFrame(infoPane.badge, ss.value + 1);
    }
  }
}

function secondsToString(seconds: number, includeZero = true) {
  if (!includeZero && seconds == 0) {
    return '';
  } else if (seconds > SECONDS_PER_MINUTE) {
    return `${Math.floor(seconds / SECONDS_PER_MINUTE)} min. ${
      seconds % SECONDS_PER_MINUTE
    } sec.`;
  } else {
    return seconds + ' seconds';
  }
}
